// Per-OTA connectivity walkthroughs shown on the Channel Manager page.
// Each guide is a short, numbered checklist the host must complete in the
// OTA's own extranet (linking Channex.io as their connectivity provider)
// BEFORE they paste their listing IDs into the PMS. This is the
// "authorise Channex" step Channex support confirmed can't be automated.
//
// Add a new OTA by appending to OTA_WALKTHROUGHS. If a provider isn't
// listed, ChannelWizard falls back to GENERIC_WALKTHROUGH.

export const GENERIC_WALKTHROUGH = {
  extranet_name: "OTA extranet",
  extranet_url: null,
  steps: [
    { title: "Open your OTA extranet", body: "Log in as the property owner/manager." },
    { title: "Find Connectivity settings", body: "Look under Account, Property Setup or Connectivity Manager." },
    { title: "Select Channex.io as your provider", body: "Search for 'Channex' in the provider list · request the connection · save." },
    { title: "Come back and paste your IDs", body: "Copy the OTA's property/hotel IDs from your dashboard and paste them below." },
  ],
};

export const OTA_WALKTHROUGHS = {
  "Booking.com": {
    extranet_name: "Booking.com Extranet",
    extranet_url: "https://admin.booking.com/",
    id_hint: "Extranet → Property → Property details → Hotel ID (numeric, e.g. 8821345)",
    steps: [
      { title: "Log in to Booking.com Extranet", body: "Open admin.booking.com and pick the property you're connecting.", link_label: "Open Extranet ↗", link_url: "https://admin.booking.com/" },
      { title: "Go to Account → Connectivity provider", body: "Top-right menu → Account → Connectivity provider. If you don't see it, contact your Booking.com Market Manager." },
      { title: "Search & connect Channex", body: "Search for 'Channex' in the provider list → click Request connection → confirm the terms → Save." },
      { title: "Wait ~30 seconds, then paste your Hotel IDs", body: "Booking.com will auto-approve. Copy the numeric Hotel ID (Property → Property details) and paste it below." },
    ],
  },
  Airbnb: {
    extranet_name: "Airbnb Host Dashboard",
    extranet_url: "https://www.airbnb.com/hosting",
    id_hint: "You don't need to paste anything — after OAuth we auto-fetch every listing on your Airbnb account.",
    oauth: true,
    oauth_endpoint: "/pms/properties/{pid}/channels/airbnb/connect",
    steps: [
      { title: "Log in to Airbnb Host Dashboard", body: "Sign in as the same host that owns the listings you want to sync.", link_label: "Open Airbnb ↗", link_url: "https://www.airbnb.com/hosting" },
      { title: "Click 'Connect Airbnb via OAuth' below", body: "We'll open Airbnb in a new tab. The consent screen will show 'Channex' — that's our certified connectivity partner." },
      { title: "Approve Channex on the Airbnb consent screen", body: "Airbnb will list the permissions Channex needs (calendar, pricing, messaging) · click Allow · you'll be returned here." },
      { title: "Your listings appear automatically", body: "No pasting IDs — every listing on your Airbnb account lands in your pool within seconds." },
    ],
  },
  Agoda: {
    extranet_name: "Agoda YCS",
    extranet_url: "https://ycs.agoda.com/",
    id_hint: "YCS → Property → Property Details → Property ID",
    steps: [
      { title: "Log in to Agoda YCS", body: "Head to ycs.agoda.com with your property manager account.", link_label: "Open YCS ↗", link_url: "https://ycs.agoda.com/" },
      { title: "Property → Connectivity", body: "Sidebar → Property → Connectivity → Channel Manager." },
      { title: "Select Channex.io & save", body: "In the provider dropdown, pick Channex.io · save the mapping. Agoda may email a confirmation — approve it." },
      { title: "Paste your Agoda Property IDs below", body: "Copy the numeric Property ID from Property → Property Details and paste below." },
    ],
  },
  Expedia: {
    extranet_name: "Expedia Partner Central",
    extranet_url: "https://www.expediapartnercentral.com/",
    id_hint: "EPC → Property → Property Details → Expedia Property ID (SCEA or numeric)",
    steps: [
      { title: "Log in to Expedia Partner Central", body: "Sign in as the property administrator.", link_label: "Open EPC ↗", link_url: "https://www.expediapartnercentral.com/" },
      { title: "Property → Room & Rates → Connectivity Settings", body: "Some accounts see it under Property Info → System Connectivity." },
      { title: "Change provider to Channex", body: "Search 'Channex' in the connectivity partner list · save. Expedia's connectivity team may take a few hours to approve." },
      { title: "Paste your Expedia Property IDs below", body: "Copy the Property ID (EPC top-left header) and paste below." },
    ],
  },
  MakeMyTrip: {
    extranet_name: "MMT Ingo Extranet",
    extranet_url: "https://ingoibibo.com/",
    id_hint: "Ingo → Property → Property Master → Hotel Code",
    steps: [
      { title: "Log in to Ingo Extranet", body: "Ingo is the shared MMT + Goibibo backend.", link_label: "Open Ingo ↗", link_url: "https://ingoibibo.com/" },
      { title: "Manage Property → Connectivity Manager", body: "Sidebar → Manage Property → Connectivity Manager → Add." },
      { title: "Add Channex.io and submit", body: "Select Channex.io from the list · paste the property token from your Channex screen (we'll show it on the next step) · Submit." },
      { title: "Copy MMT Hotel Code below", body: "Dashboard → Property Master → copy the Hotel Code and paste below." },
    ],
  },
  Goibibo: {
    extranet_name: "Ingo Extranet (shared with MMT)",
    extranet_url: "https://ingoibibo.com/",
    id_hint: "Same Hotel Code as MakeMyTrip — Ingo → Property Master",
    steps: [
      { title: "Log in to Ingo Extranet", body: "Goibibo shares the same backend as MakeMyTrip.", link_label: "Open Ingo ↗", link_url: "https://ingoibibo.com/" },
      { title: "Manage Property → Connectivity Manager", body: "Skip this step if you already did it for MakeMyTrip — one connection covers both channels." },
      { title: "Add Channex.io as your provider", body: "Select Channex.io · paste the token · Submit." },
      { title: "Paste your Goibibo Hotel Code below", body: "Same Hotel Code as MMT — Property Master → Hotel Code." },
    ],
  },
  VRBO: {
    extranet_name: "VRBO Owner Dashboard",
    extranet_url: "https://www.vrbo.com/owner",
    id_hint: "Dashboard → Listing → the numeric ID in the URL after /property/",
    steps: [
      { title: "Log in to VRBO Owner Dashboard", body: "Sign in as the property owner.", link_label: "Open VRBO ↗", link_url: "https://www.vrbo.com/owner" },
      { title: "Property → Software Providers", body: "Sidebar → Property → Software Providers → Add provider." },
      { title: "Authorise Channex", body: "Search Channex · Authorise · Save. VRBO uses an OAuth-style approval — you may need to sign in again." },
      { title: "Copy your VRBO listing IDs below", body: "Grab the numeric ID from your listing URL (…/property/<ID>) and paste below." },
    ],
  },
  "Trip.com": {
    extranet_name: "Trip.com Ebooking",
    extranet_url: "https://ebooking.trip.com/",
    id_hint: "Ebooking → Property → Property Setup → Hotel ID (numeric)",
    steps: [
      { title: "Log in to Trip.com Ebooking", body: "Sign in with your Ebooking property manager account.", link_label: "Open Ebooking ↗", link_url: "https://ebooking.trip.com/" },
      { title: "Property Setup → Connectivity Provider", body: "Sidebar → Property Setup → Connectivity Provider → Change." },
      { title: "Select Channex & save", body: "Pick Channex from the dropdown · confirm on Trip.com's popup · save." },
      { title: "Paste your Trip.com Hotel IDs below", body: "Copy the numeric Hotel ID from Property Setup and paste below." },
    ],
  },
};

export function walkthroughFor(provider) {
  return OTA_WALKTHROUGHS[provider] || GENERIC_WALKTHROUGH;
}
